import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useSelector } from "react-redux";
import { urls } from "../../urls/urls";
import { BASE_URL_TEST } from "../../utils/baseUrl";

export default function PaymentEditModal({ open, onClose, paymentData, onSuccess }) {
 // const apptoken = useSelector((state) => state.user.apptoken);
  const loginToken = useSelector((state) => state.user.logintoken);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    payment_method: "",
    description: "",
    transaction_id: "",
    payment_date: "",
    status: "pending",
    payment_receipt: null,
  });

  const [fileName, setFileName] = useState("");

  useEffect(() => {
  if (paymentData && open) {
    setFormData({
      payment_method: paymentData.payment_method ?? "",
      description: paymentData.description ?? "",
      transaction_id: paymentData.transaction_id ?? "",
      payment_date: paymentData.payment_date
        ? paymentData.payment_date.split("T")[0]
        : "",
      status: paymentData.status ?? "pending",
      payment_receipt: null,
    });
    setFileName(paymentData.payment_receipt || "");
    setErrors({});  // ← clear errors on open
  }
}, [paymentData, open]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        payment_receipt: file,
      });
      setFileName(file.name);
    }
  };

  // Add this state near other states
const [errors, setErrors] = useState({});

// Validation function
const validate = () => {
  const newErrors = {};

  if (!formData.payment_method) {
    newErrors.payment_method = "Payment method is required.";
  }
  if (!formData.transaction_id || formData.transaction_id.trim() === "") {
    newErrors.transaction_id = "Transaction ID is required.";
  }
  if (!formData.payment_date) {
    newErrors.payment_date = "Payment date is required.";
  }
  if (!formData.description || formData.description.trim() === "") {
    newErrors.description = "Description is required.";
  }
  if (!formData.status) {
    newErrors.status = "Status is required.";
  }
  if (!formData.payment_receipt && !fileName) {
    newErrors.payment_receipt = "Payment receipt is required.";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

// Update handleSubmit
const handleSubmit = async () => {
  if (!validate()) return;  // ← stop if validation fails

  if (!paymentData?.vpayment_id) {
    console.error("Payment ID is missing");
    return;
  }

  setLoading(true);
  try {
    const formDataToSend = new FormData();
    formDataToSend.append("payment_method", formData.payment_method);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("transaction_id", formData.transaction_id);
    formDataToSend.append("payment_date", formData.payment_date);
    formDataToSend.append("status", formData.status);
    if (formData.payment_receipt) {
      formDataToSend.append("payment_receipt", formData.payment_receipt);
    }

    const response = await fetch(
      BASE_URL_TEST + urls("updateWithdrawStatus", paymentData.vpayment_id),
      {
        method: "PUT",
        headers: {
          Authorization: loginToken ? `Bearer ${loginToken}` : "",
        },
        body: formDataToSend,
      }
    );

    const result = await response.json();
    if (response.ok && result.status) {
      if (onSuccess) onSuccess();
      onClose();
    } else {
      console.error("Failed to update payment:", result.message);
    }
  } catch (error) {
    console.error("Error updating payment:", error);
  } finally {
    setLoading(false);
  }
};
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Edit Payment Details
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

    <DialogContent sx={{ mt: 2 }}>
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
    
    {/* Vendor Info - read only */}
    <Box sx={{ display: "flex", gap: 2 }}>
      <TextField
        label="Vendor ID"
        value={paymentData?.vendorDetails?.vendor_id || ""}
        fullWidth
        disabled
        variant="outlined"
      />
      <TextField
        label="Shop Name"
        value={paymentData?.vendorDetails?.shop_name || ""}
        fullWidth
        disabled
        variant="outlined"
      />
    </Box>

    <Box sx={{ display: "flex", gap: 2 }}>
      <TextField
        label="Amount"
        value={paymentData?.amount ? `₹${parseFloat(paymentData.amount).toLocaleString("en-IN")}` : ""}
        fullWidth
        disabled
        variant="outlined"
      />
      <TextField
        label="Bank Details"
        value={
          paymentData?.vendorDetails?.bankDetails?.length > 0
            ? `${paymentData.vendorDetails.bankDetails[0].bank_name} - ${paymentData.vendorDetails.bankDetails[0].account_number}`
            : "N/A"
        }
        fullWidth
        disabled
        variant="outlined"
      />
    </Box>

    {/* Editable fields */}
  <FormControl fullWidth error={!!errors.payment_method}>
  <InputLabel>Payment Method</InputLabel>
  <Select
    value={formData.payment_method}
    onChange={(e) => {
      handleChange("payment_method")(e);
      setErrors((prev) => ({ ...prev, payment_method: "" }));
    }}
    label="Payment Method"
  >
    <MenuItem value="cash">Cash</MenuItem>
    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
    <MenuItem value="upi">UPI</MenuItem>
    <MenuItem value="cheque">Cheque</MenuItem>
  </Select>
  {errors.payment_method && (
    <Typography sx={{ color: "red", fontSize: 12, mt: 0.5 }}>
      {errors.payment_method}
    </Typography>
  )}
</FormControl>

<TextField
  label="Transaction ID"
  value={formData.transaction_id}
  onChange={(e) => {
    handleChange("transaction_id")(e);
    setErrors((prev) => ({ ...prev, transaction_id: "" }));
  }}
  fullWidth
  variant="outlined"
  placeholder="Enter transaction ID"
  error={!!errors.transaction_id}
  helperText={errors.transaction_id}
/>

<TextField
  label="Payment Date"
  type="date"
  value={formData.payment_date}
  onChange={(e) => {
    handleChange("payment_date")(e);
    setErrors((prev) => ({ ...prev, payment_date: "" }));
  }}
  fullWidth
  variant="outlined"
  InputLabelProps={{ shrink: true }}
  error={!!errors.payment_date}
  helperText={errors.payment_date}
/>

<TextField
  label="Description"
  value={formData.description}
  onChange={(e) => {
    handleChange("description")(e);
    setErrors((prev) => ({ ...prev, description: "" }));
  }}
  fullWidth
  variant="outlined"
  multiline
  rows={2}
  placeholder="Enter description"
  error={!!errors.description}
  helperText={errors.description}
/>

<FormControl fullWidth error={!!errors.status}>
  <InputLabel>Status</InputLabel>
  <Select
    value={formData.status}
    onChange={(e) => {
      handleChange("status")(e);
      setErrors((prev) => ({ ...prev, status: "" }));
    }}
    label="Status"
  >
    <MenuItem value="pending">Pending</MenuItem>
    <MenuItem value="on process">On Process</MenuItem>
    <MenuItem value="completed">Completed</MenuItem>
  </Select>
  {errors.status && (
    <Typography sx={{ color: "red", fontSize: 12, mt: 0.5 }}>
      {errors.status}
    </Typography>
  )}
</FormControl>

{/* File Upload */}
<Box>
  <Button
    component="label"
    variant="outlined"
    startIcon={<CloudUploadIcon />}
    sx={{
      textTransform: "none",
      borderRadius: "8px",
      borderColor: errors.payment_receipt ? "red" : "#2c666e",
      color: errors.payment_receipt ? "red" : "#2c666e",
    }}
  >
    Upload Payment Receipt
    <input
      type="file"
      hidden
      accept="image/*,.pdf"
      onChange={(e) => {
        handleFileUpload(e);
        setErrors((prev) => ({ ...prev, payment_receipt: "" }));
      }}
    />
  </Button>
  {errors.payment_receipt && (
    <Typography sx={{ color: "red", fontSize: 12, mt: 0.5 }}>
      {errors.payment_receipt}
    </Typography>
  )}
  {fileName && (
    <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
      {fileName.includes("_") ? `Existing receipt: ${fileName}` : `Selected file: ${fileName}`}
    </Typography>
  )}
</Box>
  </Box>
</DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: "#f5f5f5" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            color: "#666",
            borderColor: "#ccc",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            bgcolor: "#2c666e",
            "&:hover": {
              bgcolor: "#245757",
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
